import React from 'react'
import { ListItem, Separator, Skeleton } from '@fs/zion-ui'
import { css } from '@emotion/core'
import { getSessionId } from '@fs/zion-session'
import { Link } from '@fs/zion-router'
import { type GroupSummary, type TreeId } from '@fs/zion-tree-types'
import noGroupImg from '../upload-page/assets/no-group.jpg'
import ProcessingFileText from '../upload-page/upload-row/ProcessingFileText'
import getTreeUrl from '../../../helpers/getTreeUrl'
import { useTreeDetails } from '../upload-page/upload-row/useTreeDetails'
import { useShowLoadingBar } from '../upload-page/upload-row/useShowLoadingBar'
import CetUploadError from '../upload-page/upload-row/CetUploadError'
import { type GedcomFileFailed, type GedcomFile } from '../../../services/groupTypes'
import ManageTreeRowActions, { ManageTreeRowActionsSkeleton } from './ManageTreeRowActions'

const imageStyleCss = css`
  img {
    border-radius: 8px;
    max-width: 79px;
  }
`

const linkStyleCss = css`
  text-decoration: none;
`

type GetTestIdParams = {
  isResearcherTree: boolean
  treeId?: TreeId | null
  groupId?: string
  fileName?: string
}

const getTestId = ({ isResearcherTree, treeId, groupId, fileName }: GetTestIdParams): string => {
  const treeType = isResearcherTree ? 'cet' : 'family-group'
  const listId = treeId || groupId || fileName
  const suffix = listId ? `:${listId}` : ''
  return `manage-tree-list:${treeType}${suffix}`
}

type ManageTreeRowProps =
  | {
      group: GroupSummary
      file?: GedcomFile
      failedFile?: never
      processingFile?: never
      isMobile: boolean
      isLast?: boolean
    }
  | {
      group?: never
      file?: never
      failedFile?: never
      processingFile: GedcomFile
      isMobile: boolean
      isLast?: boolean
    }
  | {
      group?: never
      file?: never
      failedFile: GedcomFileFailed
      processingFile?: never
      isMobile: boolean
      isLast?: boolean
    }

function ManageTreeRow({ group, file, failedFile, processingFile, isMobile, isLast }: ManageTreeRowProps): JSX.Element {
  // Extract values for hooks (must be called before any conditional returns)
  const treeId = group?.treeIds?.[0]
  const fileStatus = file?.fileStatus ?? processingFile?.fileStatus
  const percentComplete = file?.percentComplete ?? processingFile?.percentComplete

  const sessionId = getSessionId()
  const { treeDetails } = useTreeDetails(treeId)
  const [{ isImporting, isProcessing }] = useShowLoadingBar({ fileStatus, percentComplete })

  if (failedFile) {
    const imageSrc = noGroupImg

    return (
      <>
        <ListItem
          data-testid={`manage-tree-list:failed-file:${failedFile.fileName}`}
          dense={isMobile}
          startElement={
            <div css={imageStyleCss}>
              <ListItem.Image size="md" src={imageSrc} />
            </div>
          }
          primaryText={failedFile.fileName}
        />
        <CetUploadError fileName={failedFile.fileName} fileStatus={failedFile.fileStatus} />
      </>
    )
  }

  if (processingFile) {
    const imageSrc = noGroupImg
    const processingFileIsProcessing = isProcessing || isImporting

    return (
      <>
        <ListItem
          data-testid={`manage-tree-list:processing-file:${processingFile.fileName}`}
          dense={isMobile}
          startElement={
            <div css={imageStyleCss}>
              <ListItem.Image size="md" src={imageSrc} />
            </div>
          }
          primaryText={
            <>
              {processingFile.title}
              <ProcessingFileText />
            </>
          }
          endElement={
            !isMobile && (
              <ManageTreeRowActions
                file={processingFile}
                group={undefined}
                isProcessingOrLoading={processingFileIsProcessing}
                isPrivateTree={undefined}
                isMobile={isMobile}
              />
            )
          }
        />
        {isMobile && (
          <>
            {processingFile.fileStatus === 'IMPORTING' && processingFile.percentComplete != null && (
              <>
                <Separator size="pico" />
                <ManageTreeRowActions
                  file={processingFile}
                  group={undefined}
                  isProcessingOrLoading={processingFileIsProcessing}
                  isPrivateTree={undefined}
                  isMobile={isMobile}
                />
              </>
            )}

            {!isLast && <Separator size="sm" />}
          </>
        )}
      </>
    )
  }

  // group is guaranteed to be defined if failedFile and processingFile are not
  const name = group.name
  const groupId = group.id
  const types = group.types
  const imageUrl = group.imageUrl
  const fileName = file?.fileName

  const imageSrc = imageUrl ? `${imageUrl}?sessionId=${sessionId}` : noGroupImg
  const isProcessingOrLoading = isProcessing || isImporting
  const isPrivateTree = treeDetails?.private
  const isResearcherTree = types?.includes('RESEARCHER_TREE')

  const treeUrl = getTreeUrl({
    treeId,
    mePersonId: treeDetails?.startingPersonId,
  })

  const testId = getTestId({ isResearcherTree, treeId, groupId, fileName })

  function getPrimaryText(): JSX.Element | string {
    if (isProcessingOrLoading && !treeId) {
      return (
        <>
          {name}
          <ProcessingFileText />
        </>
      )
    }
    return (
      <Link css={linkStyleCss} to={treeUrl} external wayfinding>
        {name}
      </Link>
    )
  }

  return (
    <>
      <ListItem
        data-testid={testId}
        dense={isMobile}
        startElement={
          // TREEWEB-11486 - We specify role="none" to avoid surfacing images to screen readers that we have not captured functional alt text for.
          <div css={imageStyleCss}>
            <ListItem.Image role="none" size="md" src={imageSrc} />
          </div>
        }
        primaryText={getPrimaryText()}
        endElement={
          isMobile ? undefined : (
            <ManageTreeRowActions
              file={file}
              group={group}
              isProcessingOrLoading={isProcessingOrLoading}
              isPrivateTree={isPrivateTree}
              isMobile={isMobile}
            />
          )
        }
      />
      {isMobile && (
        <>
          <Separator size="pico" />

          <ManageTreeRowActions
            file={file}
            group={group}
            isProcessingOrLoading={isProcessingOrLoading}
            isPrivateTree={isPrivateTree}
            isMobile={isMobile}
          />

          {!isLast && <Separator size="sm" />}
        </>
      )}
    </>
  )
}

export { ManageTreeRow }

export function ManageTreeRowSkeleton({ isMobile }: { isMobile: boolean }): JSX.Element {
  return (
    <>
      <ListItem
        aria-hidden
        dense={isMobile}
        startElement={<Skeleton.Image width={79} height={56} />}
        primaryText={<Skeleton.TypeSkeleton maxWidth={250} randomSeed={3} />}
        endElement={isMobile ? undefined : <ManageTreeRowActionsSkeleton />}
        data-testid="ManageTreeRowSkeleton"
      />

      {isMobile && (
        <>
          <Separator size="pico" />
          <ManageTreeRowActionsSkeleton isMobile />
        </>
      )}
    </>
  )
}
